import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import MenuPage from '../Menu/MenuPage';
import SuppliesPage from '../Supplies/SuppliesPage';
import SalesPage from '../Sales/SalesPage';
import CostingPage from '../Costing/CostingPage';
import AdminPage from '../Admin/AdminPage';
import AnalysisPage from '../Analysis/AnalysisPage';
import CostStructurePage from '../CostStructure/CostStructurePage';
import InventoryPage from '../Inventory/InventoryPage';
import POSPage from '../POS/POSPage';
import DashboardPage from '../Dashboard/DashboardPage';
import ComandasPage from '../Orders/ComandasPage';
import TicketSettingsPage from '../Settings/TicketSettingsPage';
import EmployeesPage from '../Employees/EmployeesPage';

const Dashboard = ({
  categories,
  menuItems,
  supplies,
  recipes,
  users,
  sales,
  supplyCategories,
  employees,
  onAddCategory,
  onDeleteCategory,
  onAddMenuItem,
  onDeleteMenuItem,
  onUpdateMenuItem,
  onAddSupply,
  onUpdateSupply,
  onDeleteSupply,
  onAddSupplyCategory,
  onDeleteSupplyCategory,
  onSaveRecipe,
  onDeleteRecipe,
  onUpdateRecipe,
  onAddUser,
  onDeleteUser,
  onUpdateUser,
  onLogout,
  onEmployeeLogout,
  currentUser,
  employeeSession,
  effectiveAccess,
  handleUpdateSaleStatus,
  onAddPOSSale,
  onAddEmployee,
  onUpdateEmployee,
  onDeleteEmployee,
  onDeleteSale,
  inventory
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const access = effectiveAccess || currentUser?.access || {};

  const [activeTab, setActiveTab] = useState(location.pathname.replace(/^\//, '') || 'pos');
  const [cajaActiva, setCajaActiva] = useState(null);

  useEffect(() => {
    const pathTab = location.pathname.replace(/^\//, '') || 'pos';
    if (activeTab !== pathTab) setActiveTab(pathTab);
  }, [location.pathname, activeTab]);

  useEffect(() => {
    if (!currentUser) return;
    if (!access?.[activeTab]) {
      const firstAllowed = Object.entries(access).find(([_, allowed]) => allowed)?.[0];
      if (firstAllowed) navigate(`/${firstAllowed}`, { replace: true });
    }
  }, [currentUser, activeTab, navigate, access]);

  useEffect(() => {
    if (!currentUser) return;
    setCajaActiva(null);
    localStorage.removeItem('cajaActiva');

    fetch('/api/get_active_cash_drawer.php')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setCajaActiva(data.data);
          localStorage.setItem('cajaActiva', JSON.stringify(data.data));
        }
      })
      .catch(err => console.error('Error al cargar caja:', err));
  }, [currentUser]);

  const handleEditCategory = (category) => {
    console.log("Editando categoría:", category);
  };

  return (
    <div className="min-h-screen bg-[#F9EFD7]">
      <Navbar
        onLogout={onLogout}
        onEmployeeLogout={onEmployeeLogout}
        userAccess={access}
        employeeSession={employeeSession}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Routes>
          <Route path="/" element={<Navigate to="/pos" replace />} />

          {access.pos && (
            <Route path="/pos" element={
              <POSPage
                menuItems={menuItems}
                categories={categories}
                sales={sales}
                cajaActiva={cajaActiva}
                onAddPOSSale={onAddPOSSale}
                employees={employees}
                onAddEmployee={onAddEmployee}
                onUpdateEmployee={onUpdateEmployee}
                onDeleteEmployee={onDeleteEmployee}
              />
            } />
          )}

          {access.employees && (
            <Route path="/employees" element={
              <EmployeesPage
                employees={employees}
                onAddEmployee={onAddEmployee}
                onUpdateEmployee={onUpdateEmployee}
                onDeleteEmployee={onDeleteEmployee}
              />
            } />
          )}

          {access.admin && (
            <Route path="/admin" element={
              <AdminPage
                users={users}
                onAddUser={onAddUser}
                onDeleteUser={onDeleteUser}
                onUpdateUser={onUpdateUser}
              />
            } />
          )}

          {access.menu && (
            <Route path="/menu" element={
              <MenuPage
                categories={categories}
                menuItems={menuItems}
                onAddCategory={onAddCategory}
                onDeleteCategory={onDeleteCategory}
                onEdit={handleEditCategory}
                onAddMenuItem={onAddMenuItem}
                onDeleteMenuItem={onDeleteMenuItem}
                onUpdateMenuItem={onUpdateMenuItem}
                onAddPOSSale={onAddPOSSale}
                supplies={supplies}  
              />
            } />
          )}

          {access.supplies && (
            <Route path="/supplies" element={
              <SuppliesPage
                supplies={supplies}
                supplyCategories={supplyCategories}
                onAddSupply={onAddSupply}
                onUpdateSupply={onUpdateSupply}
                onDeleteSupply={onDeleteSupply}
                onAddSupplyCategory={onAddSupplyCategory}
                onDeleteSupplyCategory={onDeleteSupplyCategory}
              />
            } />
          )}

          {access.sales && (
            <Route path="/sales" element={
              <SalesPage
                menuItems={menuItems}
                sales={sales}
                onUpdateSaleStatus={handleUpdateSaleStatus}
                onUpdateSales={handleUpdateSaleStatus}
                onDeleteSale={onDeleteSale}
                recipes={recipes}
              />
            } />
          )}

          {access.costing && (
            <Route path="/costing" element={
              <CostingPage
                menuItems={menuItems}
                categories={categories}
                supplies={supplies}
                recipes={recipes}
                onSaveRecipe={onSaveRecipe}
                onDeleteRecipe={onDeleteRecipe}
                onUpdateRecipe={onUpdateRecipe}
              />
            } />
          )}

          {access.analysis && (
            <Route path="/analysis" element={
              <AnalysisPage
                categories={categories}
                menuItems={menuItems}
                sales={sales}
                recipes={recipes}
              />
            } />
          )}

          {access.costStructure && (
            <Route path="/costStructure" element={
              <CostStructurePage
                sales={sales}
                recipes={recipes}
              />
            } />
          )}

          {access.inventory && (
            <Route path="/inventory" element={
              <InventoryPage
                supplies={supplies}
                sales={sales}
                recipes={recipes}
                onUpdateSupply={onUpdateSupply}
                supplyCategories={supplyCategories}
              />
            } />
          )}

          {access.comandas && (
            <Route path="/comandas" element={
              <ComandasPage
                sales={sales}
                employees={employees}
                onUpdateSaleStatus={handleUpdateSaleStatus}
              />
            } />
          )}

          {access.dashboard && (
            <Route path="/dashboard" element={
              <DashboardPage
                sales={sales}
                recipes={recipes}
                inventory={supplies}
              />
            } />
          )}

          {access.ticketSettings && (
            <Route path="/ticketSettings" element={
              <TicketSettingsPage />
            } />
          )}

          <Route path="*" element={
            <div className="mt-6 text-center text-[#3F1907]">
              No tienes permisos para ver esta página o la ruta no existe.
            </div>
          } />
        </Routes>
      </div>
    </div>
  );
};

export default Dashboard;